<template>
  <v-row>
    <v-col
      cols="12"
    >
      <v-card :loading="loading">
        <v-card-title class="text-subtitle-2">
          {{ $t('common.account_details') }}
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-form
            ref="account"
            @submit.prevent="saveAccount()"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="business"
                  name="company_name"
                  :label="$t('common.company_name')"
                  :rules="rules.company_name"
                  validate-on-blur
                  :error-messages="errors.company_name"
                  v-model="accountData.company_name"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="mdi-office-building-marker"
                  name="account_name"
                  :label="$t('account.account_name')"
                  :rules="rules.company_name"
                  validate-on-blur
                  :error-messages="errors.company_name"
                  v-model="accountData.account_name"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="room"
                  name="address_1"
                  :label="$t('common.address_1')"
                  :rules="rules.address_1"
                  validate-on-blur
                  :error-messages="errors.address_1"
                  v-model="accountData.address_1"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="room"
                  name="address_2"
                  :label="$t('common.address_2')"
                  :rules="rules.address_2"
                  validate-on-blur
                  :error-messages="errors.address_2"
                  v-model="accountData.address_2"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="room"
                  name="address_3"
                  :label="$t('common.address_3')"
                  :rules="rules.address_3"
                  validate-on-blur
                  :error-messages="errors.address_3"
                  v-model="accountData.address_3"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="room"
                  name="address_4"
                  :label="$t('common.address_4')"
                  :rules="rules.address_4"
                  validate-on-blur
                  :error-messages="errors.address_4"
                  v-model="accountData.address_4"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="room"
                  name="post_code"
                  :label="$t('common.post_code')"
                  :rules="rules.post_code"
                  validate-on-blur
                  :error-messages="errors.post_code"
                  v-model="accountData.post_code"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                v-if="accountPhoneEnabled"
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="phone"
                  name="phone"
                  :label="$t('common.phone')"
                  :rules="rules.phone"
                  validate-on-blur
                  :error-messages="errors.phone"
                  v-model="accountData.phone"
                  :disabled="!canView"
                />
              </v-col>
            </v-row>
            <v-row v-if="canManageExpiryPolicy">
              <v-col
                cols="12"
                lg="12"
                md="12"
                sm="12"
              >
                <v-alert
                  class="ma-1"
                  type="info"
                  outlined
                  dense
                  prominent
                  border="left"
                >
                  <v-row align="center">
                    <v-col
                      cols="12"
                      sm="12"
                      md="6"
                      lg="6"
                    >
                      <div>
                        {{ $t('account.expiry_policy') }}
                      </div>
                    </v-col>
                    <v-col
                      cols="12"
                      lg="6"
                      md="6"
                      sm="10"
                    >
                      <v-select
                        prepend-icon="mdi-calendar-lock"
                        name="expiry_policy"
                        :items="policyOptions"
                        v-model="accountData.expiry_policy"
                        :disabled="!canView"
                      >
                        <template #selection="{ item }">
                          <span
                            v-if="item === 0"
                          >
                            {{ $t('account.expiry_default') }}
                          </span>
                          <span
                            v-else
                          >
                            {{ $t('account.expiry_policy_options_text', { days: item }) }}
                          </span>
                        </template>
                        <template #item="{ item }">
                          <span
                            v-if="item === 0"
                          >
                            {{ $t('account.expiry_default') }}
                          </span>
                          <span
                            v-else
                          >
                            {{ $t('account.expiry_policy_options_text', { days: item }) }}
                          </span>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="10"
                lg="10"
                md="8"
                sm="8"
              >
                <v-text-field
                  prepend-icon="mdi-certificate"
                  name="license"
                  :label="$t('common.license')"
                  :rules="rules.license"
                  validate-on-blur
                  :error-messages="errors.license"
                  v-model="accountData.license"
                  disabled
                />
              </v-col>
              <v-col
                cols="2"
                lg="2"
                md="4"
                sm="4"
                class="text-right"
              >
                <v-btn
                  block
                  @click="navigateToLicenses()"
                >
                  {{ $t('common.license_view_or_change') }}
                  <v-icon
                    right
                    dark
                  >
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-switch
                  name="twilio_enabled"
                  :label="'Enable Auto-Dialer for Outgoing Voice Calling (Twilio API)'"
                  :rules="rules.twilio_enabled"
                  v-model="accountData.twilio_enabled"
                  :disabled="!canView"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="api"
                  name="twilio_sid"
                  :label="'Twilio SID'"
                  :rules="rules.twilio_sid"
                  validate-on-blur
                  v-model="accountData.twilio_sid"
                  :error-messages="errors.twilio_sid"
                  :disabled="!canView || !accountData.twilio_enabled"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  type="password"
                  prepend-icon="key"
                  name="twilio_auth_token"
                  :label="'Twilio Auth Token (Hidden, Click to change)'"
                  :rules="rules.twilio_auth_token"
                  validate-on-blur
                  :error-messages="errors.twilio_auth_token"
                  v-model="accountData.twilio_auth_token"
                  :disabled="!canView || !accountData.twilio_enabled"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  type="phone"
                  prepend-icon="phone"
                  name="twilio_number"
                  :label="'Twilio Outgoing Number'"
                  :rules="rules.twilio_number"
                  validate-on-blur
                  :error-messages="errors.twilio_number"
                  v-model="accountData.twilio_number"
                  :disabled="!canView || !accountData.twilio_enabled"
                />
              </v-col>
            </v-row>
            <v-col>
              <span>{{ $t('auth.enable_mfa') }}</span>
              <v-switch
                v-model="accountData.mfa_enabled"
                name="mfa_enabled"
                :label="$t('auth.enable_mfa_switch')"
                :loading="loading"
                :disabled="loading"
                :true-value="true"
                :false-value="false"
                @change="enableMFA"
              />
            </v-col>
            <v-card-actions>
              <v-btn
                type="submit"
                color="primary"
                :disabled="!canView"
              >
                {{ $t('common.save') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import url from '../../../utils/url';
import { ruleIsValidExpiryDays, ruleRequired } from '../../../utils/rules';

export default {
  name: 'AccountDetails',
  props: {
    accountName: {
      type: String,
    },
  },
  data() {
    return {
      loading: true,
      accountPhoneEnabled: false, // Disabled for GDPR
      accountData: {},
      errors: {
        company_name: [],
        account_name: [],
        address_1: [],
        address_2: [],
        address_3: [],
        address_4: [],
        post_code: [],
        phone: [],
        license: [],
        twilio_enabled: [],
        twilio_sid: [],
        twilio_auth_token: [],
        twilio_number: [],
        expiry_policy: [],
        mfa_enabled: [],
      },
    };
  },
  computed: {
    policyOptions() {
      return this.$store.getters['account/expiry_policy_options'];
    },
    ...mapGetters(['user', 'config', 'formatDate', 'can', 'app', 'languages', 'dateFormats', 'views', 'administrator', 'license']),
    accountUrl() {
      return url`/api/v1/accounts/${this.accountName}`;
    },
    rules() {
      return {
        company_name: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.company_name') })),
        ],
        account_name: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.account_name') })),
        ],
        address_1: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.address_1') })),
        ],
        address_2: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.address_2') })),
        ],
        address_3: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.address_3') })),
        ],
        address_4: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.address_4') })),
        ],
        post_code: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.post_code') })),
          // rulePostCode(this.$t('validation.account.post_code', { attribute: this.$t('common.post_code') })),
        ],
        phone: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.phone') })),
        ],
        license: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.license') })),
        ],
        expiry_policy: [
          ruleIsValidExpiryDays(this.$t('validation.account.password_expiry', { attribute: this.$t('common.expiration_date') })),
        ],
        twilio_enabled: [],
        twilio_sid: [],
        twilio_auth_token: [],
        twilio_number: [],
      };
    },
    // map actions to get the expiry policy options

    /**
     * Can the user view the account details
     * @returns {boolean}
     */
    canView() {
      // the user can only view if they have the can('accounts') permission or the administrator flag is true
      return this.can('accounts') || this.administrator;
    },
    /**
     * Can the user manage the expiry policy
     * @returns {boolean}
     */
    canManageExpiryPolicy() {
      return this.license.level === 'premium';
    },
  },
  async created() {
    try {
      const { data } = await this.http.get(this.accountUrl);
      this.accountData = {
        ...data.account,
        license: data.account.license.license,
      };
      await this.getPolicyOptions();
    } catch (e) {
      this.error(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions(['success', 'error']),
    ...mapActions('account', { getPolicyOptions: 'GET_EXPIRY_POLICY_OPTIONS' }),
    resetErrors() {
      Object.keys(this.errors).forEach(field => {
        this.errors[field] = [];
      });
    },
    navigateToLicenses() {
      this.$router.push({ name: 'account.license' });
    },
    async saveAccount() {
      if (!this.$refs.account.validate()) return;

      try {
        this.loading = true;
        this.resetErrors();

        // Validate Twilio credentials
        if (this.accountData.twilio_enabled) {
          const { twilio_sid, twilio_auth_token } = this.accountData;
          const { data: isValid } = await axios.post('/api/v1/validate-twilio', {
            twilio_sid,
            twilio_auth_token,
          });

          if (!isValid) {
            throw new Error(this.$t('validation.twilio.invalid_credentials'));
          }
        }

        const {
          company_name,
          account_name,
          address_1,
          address_2,
          address_3,
          address_4,
          post_code,
          expiry_policy,
          phone,
          license,
          twilio_enabled,
          twilio_sid,
          twilio_auth_token,
          twilio_number,
          mfa_enabled,
        } = this.accountData;
        const { status, data } = await this.http.post(this.accountUrl, {
          company_name,
          account_name,
          address_1,
          address_2,
          address_3,
          address_4,
          post_code,
          expiry_policy,
          phone,
          license,
          twilio_enabled,
          twilio_sid,
          twilio_auth_token,
          twilio_number,
          mfa_enabled,
        });

        if (status === 200) {
          this.accountData = {
            ...data.account,
            license: data.account.license.license,
          };
          this.success(this.$t('common.saved'));
          this.$router.go(0);
          return;
        }

        throw new Error('Error Saving Account!');
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          const { errors = {} } = e.response.data;
          Object.keys(this.errors).forEach(field => {
            this.errors[field] = errors[field] || [];
          });
          return;
        }
        this.error(e);
      } finally {
        this.loading = false;
      }
    },
    async enableMFA() {
      this.loading = true;
      try {
        await axios.post(`/api/v1/accounts/${this.accountName}/enable-mfa`);
        this.mfaEnabled = true;
      } catch (error) {
        this.mfaEnabled = false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
